// Линейная запись формул Word (UnicodeMath) и обычный текст с символами Юникода -> LaTeX.
//   «x^2/(y+1)», «a_1+b», «√(x^2+1)», «∑_(i=1)^n▒x_i», «lim┬(x→0)⁡〖sin⁡x/x〗», «■(a&b@c&d)», «x² + y² = r²»,
//   текст формулы, скопированной из Word (буквы 𝑥 𝑦 из блока математических символов).
import { FUNC_NAMES, GREEK_BY_CHAR, SYM, SYM_BY_CHAR, GREEK } from './latex';

// ------------------------------------------------------------------------------------------------ нормализация
/** Математические буквы Юникода (𝑥, 𝐀, 𝛼 …) -> обычные. */
export function normalizeMathAlnum(s: string): string {
  let out = '';
  for (const ch of s) {
    const cp = ch.codePointAt(0)!;
    if (cp === 0x210E) { out += 'h'; continue; }
    if (cp >= 0x1D400 && cp <= 0x1D6A3) {
      const k = (cp - 0x1D400) % 52;
      out += k < 26 ? String.fromCharCode(65 + k) : String.fromCharCode(97 + k - 26);
      continue;
    }
    if (cp >= 0x1D6A8 && cp <= 0x1D7C9) {
      const k = (cp - 0x1D6A8) % 58;
      const UP = 'ΑΒΓΔΕΖΗΘΙΚΛΜΝΞΟΠΡϴΣΤΥΦΧΨΩ';
      const LO = 'αβγδεζηθικλμνξοπρςστυφχψω';
      if (k < 25) out += UP[k];
      else if (k === 25) out += '∇';
      else if (k < 51) out += LO[k - 26];
      else out += '∂ϵϑϰϕϱϖ'[k - 51] || '';
      continue;
    }
    if (cp >= 0x1D7CE && cp <= 0x1D7FF) { out += String((cp - 0x1D7CE) % 10); continue; }
    // двойные буквы ℝ ℕ … оставляем
    out += ch;
  }
  return out;
}

const SUP: Record<string, string> = { '⁰': '0', '¹': '1', '²': '2', '³': '3', '⁴': '4', '⁵': '5', '⁶': '6', '⁷': '7', '⁸': '8',
  '⁹': '9', '⁺': '+', '⁻': '-', '⁼': '=', '⁽': '(', '⁾': ')', 'ⁿ': 'n', 'ⁱ': 'i', '′': "'" };
const SUB: Record<string, string> = { '₀': '0', '₁': '1', '₂': '2', '₃': '3', '₄': '4', '₅': '5', '₆': '6', '₇': '7', '₈': '8',
  '₉': '9', '₊': '+', '₋': '-', '₌': '=', '₍': '(', '₎': ')', 'ₐ': 'a', 'ₑ': 'e', 'ₒ': 'o', 'ₓ': 'x', 'ₕ': 'h', 'ₖ': 'k',
  'ₗ': 'l', 'ₘ': 'm', 'ₙ': 'n', 'ₚ': 'p', 'ₛ': 's', 'ₜ': 't', 'ᵢ': 'i', 'ⱼ': 'j' };
const COMBINING: Record<string, string> = { '̂': 'hat', '̃': 'tilde', '̄': 'bar', '̅': 'bar',
  '̇': 'dot', '̈': 'ddot', '⃗': 'vec', '⃑': 'vec', '̌': 'check', '̆': 'breve', '̿': 'overline' };
const NARY_CH: Record<string, string> = { '∑': 'sum', '∏': 'prod', '∐': 'coprod', '∫': 'int', '∬': 'iint', '∭': 'iiint',
  '∮': 'oint', '⋃': 'bigcup', '⋂': 'bigcap', '⋁': 'bigvee', '⋀': 'bigwedge' };
const OPEN: Record<string, string> = { '(': ')', '[': ']', '{': '}', '⟨': '⟩', '〖': '〗', '⌈': '⌉', '⌊': '⌋' };
const DIGRAPH: [string, string][] = [['<=>', '⇔'], ['<->', '↔'], ['->', '→'], ['<-', '←'], ['=>', '⇒'], ['<=', '≤'],
  ['>=', '≥'], ['!=', '≠'], ['<>', '≠'], ['+-', '±'], ['-+', '∓'], ['...', '…'], ['~=', '≈'], ['==', '≡']];
/** Знаки операций и соотношений (не множители). */
const OPS = new Set(['+', '-', '−', '–', '=', '<', '>', '±', '∓', '×', '·', '⋅', '∙', '÷', '≤', '≥', '≠', '≈', '≡', '∼', '≅',
  '∝', '→', '←', '↔', '⇒', '⇔', '⇐', '⇄', '⇌', '∈', '∉', '⊂', '⊃', '⊆', '⊇', '∪', '∩', '∧', '∨', '∖', ',', ';', ':', '…',
  '⩽', '⩾', '≪', '≫', '∓', '↦', '⟶', '∗', '*', '∘', '⊕', '⊗', '⊥', '∥', '|']);

interface Item { k: 'op' | 'fac'; tex: string; inner?: string }

// ------------------------------------------------------------------------------------------------ разбор
class UM {
  s: string[];
  i = 0;
  constructor(src: string) {
    let t = normalizeMathAlnum(src).replace(/[⁢⁣⁤​]/g, '').replace(/ /g, ' ');
    for (const [a, b] of DIGRAPH) t = t.split(a).join(b);
    this.s = [...t];
  }
  peek(k = 0) { return this.s[this.i + k]; }
  eof() { return this.i >= this.s.length; }
  skipSp() { while (!this.eof() && /\s/.test(this.peek())) this.i++; }

  /** Выражение до одного из стоп-символов. */
  expr(stop: Set<string>): string {
    const items: Item[] = [];
    while (!this.eof()) {
      const ch = this.peek();
      if (stop.has(ch)) break;
      if (/\s/.test(ch)) { this.i++; continue; }
      if (ch === '/' ) { this.i++; items.push({ k: 'op', tex: '/' }); continue; }
      if (OPS.has(ch) && !(ch === '|' && this.isAbsOpen())) {
        this.i++;
        items.push({ k: 'op', tex: opTex(ch) });
        continue;
      }
      const f = this.factor();
      if (f) items.push(f); else this.i++;
    }
    return foldFractions(items);
  }

  /** «|» открывает модуль, если дальше есть парная «|». */
  isAbsOpen(): boolean {
    for (let j = this.i + 1; j < this.s.length; j++) if (this.s[j] === '|') return j > this.i + 1;
    return false;
  }

  /** Множитель с индексами/степенями. */
  factor(): Item | null {
    let base = this.primary();
    if (!base) return null;
    // индексы, степени, акценты, штрихи, «под/над»
    for (;;) {
      const ch = this.peek();
      if (ch === '^' || ch === '_') {
        this.i++;
        const a = this.scriptArg();
        base = { k: 'fac', tex: attach(base.tex, ch, a) };
        continue;
      }
      if (ch !== undefined && ch in SUP) {
        let t = '';
        while (!this.eof() && this.peek() in SUP) t += SUP[this.s[this.i++]];
        if (/^'+$/.test(t)) { base = { k: 'fac', tex: base.tex + t }; continue; }
        base = { k: 'fac', tex: attach(base.tex, '^', convertLinear(t)) };
        continue;
      }
      if (ch !== undefined && ch in SUB) {
        let t = '';
        while (!this.eof() && this.peek() in SUB) t += SUB[this.s[this.i++]];
        base = { k: 'fac', tex: attach(base.tex, '_', convertLinear(t)) };
        continue;
      }
      if (ch !== undefined && ch in COMBINING) {
        this.i++;
        base = { k: 'fac', tex: '\\' + COMBINING[ch] + '{' + base.tex + '}' };
        continue;
      }
      if (ch === '′' || ch === "'") { this.i++; base = { k: 'fac', tex: base.tex + "'" }; continue; }
      if (ch === '″') { this.i++; base = { k: 'fac', tex: base.tex + "''" }; continue; }
      if (ch === '!') { this.i++; base = { k: 'fac', tex: base.tex + '!' }; continue; }
      if (ch === '┬' || ch === '┴') {
        this.i++;
        const a = this.scriptArg();
        if (/^\\(lim|max|min|sup|inf)$/.test(base.tex)) base = { k: 'fac', tex: base.tex + (ch === '┬' ? '_' : '^') + '{' + a + '}' };
        else base = { k: 'fac', tex: (ch === '┬' ? '\\underset{' : '\\overset{') + a + '}{' + base.tex + '}' };
        continue;
      }
      if (ch === '⁡') {
        // применение функции: sin⁡x, lim┬(x→0)⁡〖…〗
        this.i++;
        this.skipSp();
        const arg = this.factor();
        base = { k: 'fac', tex: base.tex + ' ' + (arg ? arg.tex : '') };
        continue;
      }
      break;
    }
    return base;
  }

  /** Аргумент индекса/степени: (…) без скобок, либо число/слово/один знак со знаком. */
  scriptArg(): string {
    const ch = this.peek();
    if (ch === undefined) return '';
    if (ch === '(' || ch === '〖' || ch === '{') {
      this.i++;
      const close = OPEN[ch];
      const e = this.expr(new Set([close]));
      if (this.peek() === close) this.i++;
      return e;
    }
    let sign = '';
    if (ch === '-' || ch === '−' || ch === '+') { sign = ch === '+' ? '+' : '-'; this.i++; }
    // число или буквенно-цифровая группа (как в Word: «x^2y» -> степень «2y»)
    let t = '';
    if (/[0-9]/.test(this.peek() || '')) {
      while (!this.eof() && /[0-9.,]/.test(this.peek()) && !(this.peek() === ',' && !/[0-9]/.test(this.peek(1) || ''))) t += this.s[this.i++];
      return sign + t.replace(/[.,]/g, '{,}');
    }
    const f = this.primary(true);
    return sign + (f ? f.tex : '');
  }

  /** Первичный элемент: число, буква, функция, скобки, корень, n-арный оператор, матрица, текст. */
  primary(inScript = false): Item | null {
    const ch = this.peek();
    if (ch === undefined) return null;
    // число с десятичной запятой/точкой
    if (/[0-9]/.test(ch)) {
      let t = '';
      while (!this.eof() && (/[0-9]/.test(this.peek()) || ((this.peek() === ',' || this.peek() === '.') && /[0-9]/.test(this.peek(1) || '')))) t += this.s[this.i++];
      return { k: 'fac', tex: t.replace(/[.,]/g, '{,}') };
    }
    // латинские буквы: функция или одна буква
    if (/[A-Za-z]/.test(ch)) {
      let j = this.i;
      let w = '';
      while (j < this.s.length && /[A-Za-z]/.test(this.s[j])) { w += this.s[j]; j++; }
      if (w === 'sqrt' && (this.s[j] === '(' || this.s[j] === '〖')) { this.i = j; return this.radical('√'); }
      const fn = FUNC_NAMES.filter((f) => w.startsWith(f)).sort((a, b) => b.length - a.length)[0];
      if (fn && (w.length === fn.length || w.length === fn.length + 1 || this.s[this.i + fn.length] === '⁡')) {
        this.i += fn.length;
        return { k: 'fac', tex: '\\' + fn };
      }
      if (inScript && w.length > 1 && w.length <= 4) { this.i = j; return { k: 'fac', tex: w }; }
      this.i++;
      return { k: 'fac', tex: ch };
    }
    // кириллица - слово целиком (индексы «вх», «max»)
    if (/[А-Яа-яЁё]/.test(ch)) {
      let t = '';
      while (!this.eof() && /[А-Яа-яЁё]/.test(this.peek())) t += this.s[this.i++];
      return { k: 'fac', tex: t };
    }
    if (ch in GREEK_BY_CHAR) { this.i++; return { k: 'fac', tex: '\\' + GREEK_BY_CHAR[ch] + ' ' }; }
    if (ch === '\\') {
      // управляющее слово линейной записи: \alpha, \sqrt, \infty …
      let j = this.i + 1;
      let w = '';
      while (j < this.s.length && /[A-Za-z]/.test(this.s[j])) { w += this.s[j]; j++; }
      this.i = j;
      if (w === 'sqrt') return this.radical('√');
      if (w in GREEK) return { k: 'fac', tex: '\\' + w + ' ' };
      if (w in SYM) {
        const u = SYM[w];
        if (u in NARY_CH) { this.i--; this.s[this.i] = u; return this.primary(); }
        if (OPS.has(u)) return { k: 'op', tex: opTex(u) } as Item;
        return { k: 'fac', tex: '\\' + w + ' ' };
      }
      return { k: 'fac', tex: '\\' + w + ' ' };
    }
    if (ch === '√' || ch === '∛' || ch === '∜') { this.i++; return this.radical(ch); }
    if (ch in NARY_CH) return this.nary();
    if (ch === '■' || ch === '█') return this.matrix();
    if (ch === '"') {
      this.i++;
      let t = '';
      while (!this.eof() && this.peek() !== '"') t += this.s[this.i++];
      this.i++;
      return { k: 'fac', tex: '\\text{' + t + '}' };
    }
    if (ch === '|' || ch === '‖') {
      this.i++;
      const e = this.expr(new Set([ch]));
      if (this.peek() === ch) this.i++;
      const d = ch === '|' ? '|' : '\\|';
      return { k: 'fac', tex: '\\left' + d + e + '\\right' + d, inner: e };
    }
    if (ch in OPEN) {
      this.i++;
      // система: {█(…)┤
      if (ch === '{' && this.peek() === '█') {
        const m = this.matrix('cases');
        if (this.peek() === '┤' || this.peek() === '}') this.i++;
        return m;
      }
      const close = OPEN[ch];
      const e = this.expr(new Set([close, '┤']));
      let cl = close;
      if (this.peek() === close || this.peek() === '┤') { if (this.peek() === '┤') cl = ''; this.i++; }
      if (ch === '〖') return { k: 'fac', tex: '{' + e + '}', inner: e };
      const L: Record<string, string> = { '(': '(', '[': '[', '{': '\\{', '⟨': '\\langle ', '⌈': '\\lceil ', '⌊': '\\lfloor ' };
      const R: Record<string, string> = { ')': ')', ']': ']', '}': '\\}', '⟩': '\\rangle ', '⌉': '\\rceil ', '⌋': '\\rfloor ', '': '.' };
      const big = /\\(frac|sum|int|prod|sqrt|begin)/.test(e);
      const tex = big || ch === '{' ? '\\left' + L[ch] + e + '\\right' + R[cl] : L[ch] + e + (R[cl] === '.' ? '' : R[cl]);
      return { k: 'fac', tex, inner: ch === '(' ? e : undefined };
    }
    if (ch in SYM_BY_CHAR) {
      this.i++;
      return { k: 'fac', tex: '\\' + SYM_BY_CHAR[ch] + ' ' };
    }
    if (ch === '°') { this.i++; return { k: 'fac', tex: '^{\\circ}' }; }
    if (/[ℝℕℤℚℂ]/.test(ch)) { this.i++; return { k: 'fac', tex: '\\mathbb{' + ({ ℝ: 'R', ℕ: 'N', ℤ: 'Z', ℚ: 'Q', ℂ: 'C' } as any)[ch] + '}' }; }
    if (/[)\]}〗⟩┤@&▒]/.test(ch)) return null;
    this.i++;
    return { k: 'fac', tex: ch };
  }

  radical(ch: string): Item {
    let deg = ch === '∛' ? '3' : ch === '∜' ? '4' : '';
    let body = '';
    const p = this.peek();
    if (p === '(' || p === '〖') {
      this.i++;
      const close = OPEN[p];
      const e = this.expr(new Set([close, '&']));
      if (this.peek() === '&') { this.i++; deg = e; body = this.expr(new Set([close])); } else body = e;
      if (this.peek() === close) this.i++;
    } else {
      const f = this.factor();
      body = f ? f.tex : '';
    }
    return { k: 'fac', tex: deg ? '\\sqrt[' + deg + ']{' + body + '}' : '\\sqrt{' + body + '}' };
  }

  nary(): Item {
    const name = NARY_CH[this.s[this.i++]];
    let sub = '';
    let sup = '';
    for (;;) {
      const ch = this.peek();
      if (ch === '_' || ch === '┬') { this.i++; sub = this.scriptArg(); continue; }
      if (ch === '^' || ch === '┴') { this.i++; sup = this.scriptArg(); continue; }
      if (ch !== undefined && ch in SUB) { let t = ''; while (!this.eof() && this.peek() in SUB) t += SUB[this.s[this.i++]]; sub = t; continue; }
      if (ch !== undefined && ch in SUP) { let t = ''; while (!this.eof() && this.peek() in SUP) t += SUP[this.s[this.i++]]; sup = t; continue; }
      break;
    }
    let body = '';
    this.skipSp();
    if (this.peek() === '▒') {
      this.i++;
      this.skipSp();
      if (this.peek() === '〖') {
        this.i++;
        body = this.expr(new Set(['〗']));
        if (this.peek() === '〗') this.i++;
      } else {
        const f = this.factor();
        body = f ? f.tex : '';
      }
    }
    let tex = '\\' + name;
    if (sub) tex += '_{' + sub + '}';
    if (sup) tex += '^{' + sup + '}';
    return { k: 'fac', tex: tex + (body ? ' ' + body : '') };
  }

  /** ■(a&b@c&d) - матрица; █(…) - система/выравнивание. */
  matrix(forceEnv?: string): Item {
    const kind = this.s[this.i++];
    if (this.peek() !== '(') return { k: 'fac', tex: '' };
    this.i++;
    const rows: string[][] = [[]];
    for (;;) {
      const e = this.expr(new Set(['&', '@', ')']));
      rows[rows.length - 1].push(e);
      const ch = this.peek();
      if (ch === '&') { this.i++; continue; }
      if (ch === '@') { this.i++; rows.push([]); continue; }
      if (ch === ')') this.i++;
      break;
    }
    const env = forceEnv || (kind === '■' ? 'matrix' : 'aligned');
    return { k: 'fac', tex: '\\begin{' + env + '}' + rows.map((r) => r.join(' & ')).join(' \\\\ ') + '\\end{' + env + '}' };
  }
}

function opTex(ch: string): string {
  const m: Record<string, string> = { '−': '-', '–': '-', '*': '\\cdot ', '∗': '\\cdot ', '⋅': '\\cdot ', '∙': '\\cdot ',
    '·': '\\cdot ', ',': ',', ';': ';', ':': ':', '<': '<', '>': '>', '|': '|' };
  if (ch in m) return m[ch];
  if (ch in SYM_BY_CHAR) return '\\' + SYM_BY_CHAR[ch] + ' ';
  return ch;
}

function attach(base: string, op: string, arg: string): string {
  const a = arg.trim();
  return base + op + (a.length === 1 ? a : '{' + a + '}');
}

/** Дроби «/»: числитель - подряд идущие множители до знака операции, знаменатель - после. */
function foldFractions(items: Item[]): string {
  const out: Item[] = [];
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    if (it.k === 'op' && it.tex === '/') {
      const num: Item[] = [];
      while (out.length && out[out.length - 1].k === 'fac') num.unshift(out.pop()!);
      const den: Item[] = [];
      let j = i + 1;
      while (j < items.length && items[j].k === 'fac') { den.push(items[j]); j++; }
      if (!num.length || !den.length) { out.push(...num, { k: 'fac', tex: '/' }, ...den); i = j - 1; continue; }
      out.push({ k: 'fac', tex: '\\frac{' + part(num) + '}{' + part(den) + '}' });
      i = j - 1;
      continue;
    }
    out.push(it);
  }
  return out.map((x) => x.tex).join('').replace(/\s+/g, ' ').trim();
}

/** Содержимое числителя/знаменателя: одна группа в скобках - без скобок. */
function part(xs: Item[]): string {
  if (xs.length === 1 && xs[0].inner !== undefined) return xs[0].inner;
  return xs.map((x) => x.tex).join('').trim();
}

/** Линейная запись / текст с символами Юникода -> LaTeX. */
export function convertLinear(src: string): string {
  const p = new UM(src || '');
  const parts: string[] = [];
  while (!p.eof()) {
    parts.push(p.expr(new Set()));
    if (!p.eof()) p.i++;                 // непарная закрывающая скобка
  }
  return parts.join(' ').replace(/\s+/g, ' ').replace(/\s+([_^])/g, '$1').trim();
}

/** Похоже на линейную запись Word / формулу из символов Юникода. */
export function looksLinear(s: string): boolean {
  return /[▒■█√∛∜∑∏∫┬┴〖〗⁡²³¹⁰⁴⁵⁶⁷⁸⁹₀₁₂₃₄₅₆₇₈₉≤≥≠≈±×·−∞→]|[\u{1D400}-\u{1D7FF}]/u.test(s)
    || /[A-Za-z0-9)]\s*[\^_]\s*[A-Za-z0-9(]/.test(s) || /[A-Za-z0-9)]\s*\/\s*[A-Za-z0-9(]/.test(s);
}
